/**
 * AdminOverseers — who oversees which province.
 *
 * A Super Admin appoints a Bishop or Overseer to a province, region or
 * district; the appointment is what gives that admin login the run of every
 * district and assembly beneath it. Appointments proposed by a sitting
 * Overseer land here for approval first.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Crown, Plus, Loader2, ArrowLeft, Check, X, Trash2, Landmark, ArrowRightLeft, History } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HX = '/api/churches-hx';
// The grouping levels an office can sit over — everything below comes with it.
const OVERSIGHT_LEVELS = ['hq', 'headquarters', 'country', 'national', 'province', 'region', 'district', 'zone'];

const api = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${r.status})`);
  }
  return r.json();
};

export default function AdminOverseers() {
  const qc = useQueryClient();
  const [appointing, setAppointing] = useState(null);
  const [handing, setHanding] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const { data: meta } = useQuery({ queryKey: ['hx-meta'], queryFn: () => api(`${HX}/meta/levels`) });
  const { data: churches = [] } = useQuery({
    queryKey: ['admin-churches-oversight'],
    queryFn: () => api('/api/admin/churches?is_active=true'),
  });
  const { data: leadership } = useQuery({
    queryKey: ['hx-leadership'],
    queryFn: () => api(`${HX}/leadership?active_only=true`),
  });
  const { data: pending } = useQuery({
    queryKey: ['hx-leadership-pending'],
    queryFn: () => api(`${HX}/leadership/pending`),
    retry: false,
  });

  const offices = (meta?.roles || []).filter((r) => r.scope === 'unit_and_below');
  const officeLabel = (key) => (meta?.roles || []).find((r) => r.key === key)?.label
    || (key || '').replace(/_/g, ' ');
  const units = useMemo(() => churches.filter(
    (c) => OVERSIGHT_LEVELS.includes((c.level || '').toLowerCase())), [churches]);
  const holders = (leadership?.items || []).filter(
    (r) => offices.some((o) => o.key === r.role_type));

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['hx-leadership'] });
    qc.invalidateQueries({ queryKey: ['hx-leadership-pending'] });
    qc.invalidateQueries({ queryKey: ['leadership-history'] });
  };

  const decide = useMutation({
    mutationFn: ({ id, verdict }) => api(`${HX}/leadership/${id}/${verdict}`, { method: 'POST' }),
    onSuccess: (_r, v) => { toast.success(v.verdict === 'approve' ? 'Appointment approved' : 'Proposal declined'); refresh(); },
    onError: (e) => toast.error(e.message),
  });
  const revoke = useMutation({
    mutationFn: (id) => api(`${HX}/leadership/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Office released'); refresh(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="admin-overseers">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin" className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mt-1">
            <Crown className="h-5 w-5 text-amber-600" /> Bishops &amp; Overseers
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            An office over a province carries its districts and assemblies with it.
            A Bishop also holds finance; an Overseer does not.
          </p>
        </div>
        <Button onClick={() => setAppointing({ user_id: '', church_id: '', role_type: 'overseer' })}
                data-testid="overseer-appoint">
          <Plus className="h-4 w-4 mr-1" /> Appoint an office
        </Button>
      </div>

      {(pending?.items || []).length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Waiting on your approval ({pending.items.length})</CardTitle>
            <CardDescription>Proposed by a sitting Bishop or Overseer inside their own province.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {pending.items.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white p-3 flex-wrap"
                   data-testid={`overseer-pending-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {p.person_name || p.person_email || p.user_id} · {officeLabel(p.role_type)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {p.church_name} ({p.church_level}) · proposed by {p.assigned_by_name || 'an overseer'}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: p.id, verdict: 'approve' })}
                          data-testid={`overseer-approve-${p.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: p.id, verdict: 'decline' })}
                          data-testid={`overseer-decline-${p.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Offices in place ({holders.length})</CardTitle>
          <CardDescription>Each holder runs everything beneath their unit — and nothing outside it.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {holders.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-400" data-testid="overseers-empty">
              No Bishops or Overseers appointed yet.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                  <th className="text-left py-2 px-3">Office</th>
                  <th className="text-left py-2 px-3">Unit</th>
                  <th className="text-left py-2 px-3">Holder</th>
                  <th className="text-right py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {holders.map((h) => (
                  <tr key={h.id} className="border-b last:border-0" data-testid={`overseer-row-${h.id}`}>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                        {officeLabel(h.role_type)}
                      </Badge>
                    </td>
                    <td className="py-2 px-3">
                      <span className="inline-flex items-center gap-1 text-slate-700">
                        <Landmark className="h-3 w-3 text-slate-400" />{h.church_name}
                      </span>
                      <span className="text-[10px] text-slate-400 ml-1 capitalize">{h.church_level}</span>
                    </td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{h.title || h.user_id}</td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex gap-1.5 justify-end">
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                                onClick={() => setHanding(h)} data-testid={`overseer-handover-${h.id}`}>
                          <ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Hand over
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600"
                                onClick={() => { if (window.confirm('Release this office?')) revoke.mutate(h.id); }}
                                data-testid={`overseer-revoke-${h.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <HandoverHistory open={showHistory} onToggle={() => setShowHistory(!showHistory)}
                       officeLabel={officeLabel} />

      {appointing && (
        <AppointDialog draft={appointing} offices={offices} units={units}
                       onClose={() => setAppointing(null)}
                       onSaved={() => { refresh(); setAppointing(null); }} />
      )}
      {handing && (
        <HandoverDialog holder={handing} officeLabel={officeLabel}
                        onClose={() => setHanding(null)}
                        onSaved={() => { refresh(); setHanding(null); }} />
      )}
    </div>
  );
}

function HandoverHistory({ open, onToggle, officeLabel }) {
  const { data } = useQuery({
    queryKey: ['leadership-history'],
    queryFn: () => api(`${HX}/leadership/history`),
    enabled: open,
    retry: false,
  });
  const rows = data?.handovers || [];
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <History className="h-4 w-4 text-slate-400" /> Handover history
            </CardTitle>
            <CardDescription>Who held each office before, and when it changed hands.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onToggle} data-testid="overseer-history-toggle">
            {open ? 'Hide' : 'Show'}
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="p-0" data-testid="overseer-history">
          {rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400" data-testid="overseer-history-empty">
              No handovers recorded yet.
            </p>
          ) : (
            <ul className="divide-y">
              {rows.map((h) => (
                <li key={h.id || h.recorded_at} className="px-4 py-3 text-sm" data-testid={`overseer-history-${h.id || h.effective_date}`}>
                  <p className="text-slate-800">
                    <span className="font-medium">{officeLabel(h.role_type)}</span> of {h.church_name}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {h.outgoing?.name || h.outgoing?.email || 'previous holder'} → {h.incoming?.name || h.incoming?.email}
                    {' '}· from {h.effective_date}
                    {h.recorded_by ? ` · recorded by ${h.recorded_by}` : ''}
                  </p>
                  {h.note && <p className="text-xs text-slate-500 italic mt-1">"{h.note}"</p>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function HandoverDialog({ holder, officeLabel, onClose, onSaved }) {
  const [form, setForm] = useState({
    successor_user_id: '',
    effective_date: new Date().toISOString().slice(0, 10),
    note: '',
  });
  const { data: admins = [] } = useQuery({
    queryKey: ['admins-for-office'], queryFn: () => api('/api/admin/admins'),
  });
  const save = useMutation({
    mutationFn: () => api(`${HX}/leadership/${holder.id}/handover`, {
      method: 'POST',
      body: JSON.stringify({
        ...form,
        successor_title: admins.find((a) => a.id === form.successor_user_id)?.name || '',
      }),
    }),
    onSuccess: () => { toast.success('Office handed over — the history is kept'); onSaved(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="overseer-handover-dialog">
        <DialogHeader>
          <DialogTitle>Hand over · {officeLabel(holder.role_type)} of {holder.church_name}</DialogTitle>
          <DialogDescription>
            The outgoing holder's term closes on this date and the successor starts. Both stay on
            the record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Successor</Label>
            <Select value={form.successor_user_id}
                    onValueChange={(v) => setForm({ ...form, successor_user_id: v })}>
              <SelectTrigger data-testid="handover-successor"><SelectValue placeholder="Choose an admin login" /></SelectTrigger>
              <SelectContent>
                {admins.filter((a) => a.id !== holder.user_id).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name || a.email} · {a.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Effective from</Label>
            <Input type="date" value={form.effective_date}
                   onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                   data-testid="handover-date" />
          </div>
          <div>
            <Label>Note <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                   placeholder="Retiring after 12 years of service" data-testid="handover-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.successor_user_id || save.isPending} onClick={() => save.mutate()}
                  data-testid="handover-save">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Hand over
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppointDialog({ draft, offices, units, onClose, onSaved }) {
  const [form, setForm] = useState(draft);
  const { data: admins = [] } = useQuery({
    queryKey: ['admins-for-office'],
    queryFn: () => api('/api/admin/admins'),
  });

  const save = useMutation({
    mutationFn: () => api(`${HX}/leadership`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: form.user_id, church_id: form.church_id, role_type: form.role_type,
        title: admins.find((a) => a.id === form.user_id)?.name || '',
        is_active: true,
      }),
    }),
    onSuccess: (res) => {
      toast.success(res?.awaiting_approval
        ? 'Proposed — a Super Admin needs to approve it'
        : 'Office appointed');
      onSaved();
    },
    onError: (e) => toast.error(e.message),
  });

  const office = offices.find((o) => o.key === form.role_type);
  const eligible = units;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="overseer-dialog">
        <DialogHeader>
          <DialogTitle>Appoint an office</DialogTitle>
          <DialogDescription>
            The holder gains the unit and everything under it, and nothing outside it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Office</Label>
            <Select value={form.role_type} onValueChange={(v) => setForm({ ...form, role_type: v })}>
              <SelectTrigger data-testid="overseer-office-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {offices.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label} <span className="text-[10px] text-slate-400 ml-1">
                      {o.carries_finance ? '· holds finance' : '· no finance'}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Unit</Label>
            <Select value={form.church_id} onValueChange={(v) => setForm({ ...form, church_id: v })}>
              <SelectTrigger data-testid="overseer-unit-select"><SelectValue placeholder="Choose a province, region or district" /></SelectTrigger>
              <SelectContent>
                {eligible.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} <span className="text-[10px] text-slate-400 ml-1 capitalize">{u.level}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {office && (
              <p className="text-[11px] text-slate-400 mt-1">
                {office.label} usually sits at <span className="capitalize">{office.default_level}</span> level.
              </p>
            )}
          </div>
          <div>
            <Label>Who holds it</Label>
            <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
              <SelectTrigger data-testid="overseer-person-select"><SelectValue placeholder="Choose an admin login" /></SelectTrigger>
              <SelectContent>
                {admins.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name || a.email} · {a.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">
              They sign in with their existing admin login — the office decides what they see.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.user_id || !form.church_id || save.isPending}
                  onClick={() => save.mutate()} data-testid="overseer-save">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Appoint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
